import 'arrayq';
import { createSelector } from 'reselect';
import { PureConnect, React } from '../../react';
import { FontIcon } from 'react-toolbox/lib/font_icon/FontIcon';
import Input from 'react-toolbox/lib/input';
import { ICategory } from '../../types';
import colors from './colors';
import { categoriesEditor } from './resource';
import cx from './style.less';
import ExtendProps from '../../focuslock/extendprops';
import windowSelectors from '../../window/selectors';
import { Btn } from '../../react-toolbox-redesign/button';

const maxCategoryNameLength = 255;

const isValidSelector = createSelector(
  categoriesEditor.selectors.item,
  (item) => item.name && item.color,
);

export default PureConnect(`CategoriesEditArea`)(
    (state) => ({
        creating: categoriesEditor.selectors.creating(state),
        isValid: isValidSelector(state),
        item: categoriesEditor.selectors.item(state),
        updating: categoriesEditor.selectors.updating(state),
        origin: state.notemenu.origin,
        screenSmall: windowSelectors.small(state),
    }),
    (dispatch) => ({
        change: (item: ICategory | any) => dispatch(categoriesEditor.actions.APPLY({ item })),
    }),
    ({ creating, updating, item, change }) => {
        const getColorName = (color: any) => {
            switch (color) {
                case '#FF062C': return 'Categoriekleur rood';
                case '#FF0062': return 'Categoriekleur roze';
                case '#ECBFEC': return 'Categoriekleur lila';
                case '#7432BB': return 'Categoriekleur paars';
                case '#3550B9': return 'Categoriekleur donkerblauw';    
                case '#00B447': return 'Categoriekleur middenzeegroen';
                case '#BFE6E1': return 'Categoriekleur faalturkoois';   
                case '#00C1D6': return 'Categoriekleur donkerturkoois';   
                case '#00ADF8': return 'Categoriekleur poederblauw';    
                case '#BFE5FD': return 'Categoriekleur lichthemelsblauw';    
                case '#68C73A': return 'Categoriekleur lentegroen';  
                case '#C6DE03': return 'Categoriekleur legergroen';   
                case '#FFFABF': return 'Categoriekleur kaki';    
                case '#FFBD00': return 'Categoriekleur guldenroede';   
                case '#FF8F00': return 'Categoriekleur donkeroranje';   
                case '#FF5C06': return 'Categoriekleur pompoenoranje';                                                                                                                                                                          
                case '#5858FF': return 'Categoriekleur ultramarineblauw';                                                                                                                                                                          
                case '#0653FF': return 'Categoriekleur oceaanblauw';                                                                                                                                                                          
              }
            return 'geen kleur';
        };

        return (
            <div className={cx(!(creating || updating) ? 'wcag-invisible' : '', 'fields-container')}  aria-hidden={!(creating || updating)} aria-labelledby= "Maak een categorie aan">
                
                <div className={cx('categorie-naam')}>
                    <span className={cx('categorie-titel')}>{creating ? 'Categorie aanmaken':'Categorie bewerken'}</span>
                    <span className={cx('categorie-input-label')}>Naam categorie<span className={cx('belangrijk')}>*</span></span>
                    <ExtendProps arialabel={'Invoer naam categorie'}>
                        <Input type="text"
                            className={cx('categorie-search')}
                            name="name"
                            value={item&&item.name ? item.name : ''}
                            maxLength={maxCategoryNameLength}
                            onChange={(value: string) => change({ name: value })}
                            floating /> 
                    </ExtendProps>
                </div>
                <div className={cx('radios')}>
                    <span className={cx('radios-label')}>Kleur<span className={cx('belangrijk')}>*</span></span>
                    <div className={cx('radios-container')}>
                        {colors.qSelect((radioColor, index) => (
                            <div className={cx('radio')} key={index}>
                                <label tabIndex={0} 
                                    className={cx('colored-radio')}                                         
                                    onKeyDown={(ev)=> {
                                        if (ev.key==='Enter')  {
                                            item.color = radioColor;
                                            return  change({ color: radioColor });}
                                        }
                                    }
                                    onChange={() => change({ color: radioColor })}                                        
                                    style={{ backgroundColor: radioColor }}>                                            
                                        <input type="radio" 
                                            name="color"                                                 
                                            value={radioColor} 
                                            checked={item.color === radioColor} 
                                            onChange={() => change({ color: radioColor })} />
                                        <div className={cx('sr-only')}>{getColorName(radioColor)}</div>
                                        {item.color === radioColor ? <FontIcon value="check" />:null}
                                </label>
                            </div>))}
                    </div>
                </div>
                
        </div>
        );
});

export const CategoriesEditAreaButtons = PureConnect(`CategoriesEditAreaButtons`)(
        (state) => ({
            creating: categoriesEditor.selectors.creating(state),
            isValid: isValidSelector(state),
            item: categoriesEditor.selectors.item(state),
            updating: categoriesEditor.selectors.updating(state),
            origin: state.notemenu.origin,
            screenSmall: windowSelectors.small(state),
        }),
        (dispatch) => ({
            cancel: () => dispatch(categoriesEditor.actions.RESET({ })),
            cancelCreating: () => dispatch(categoriesEditor.actions.CREATE_CANCEL({ })),
            cancelUpdating: () => dispatch(categoriesEditor.actions.UPDATE_CANCEL({ })),
            change: (item: ICategory | any) => dispatch(categoriesEditor.actions.APPLY({ item })),
            saveCreating: (item: ICategory) => dispatch(categoriesEditor.actions.CREATE_CONTINUE({ item })),
            saveUpdating: (item: ICategory) => dispatch(categoriesEditor.actions.UPDATE_CONTINUE({ item })),
        }),
        ({ creating, updating, item, saveCreating, cancelCreating, saveUpdating, cancelUpdating }) => {
            const onCancel = creating ? cancelCreating : cancelUpdating;
            const onSave = creating ? () => saveCreating(fixNoteDevelopmentJsonServer(item)) : () => saveUpdating(item);
            
            var canBeSaved = !!(item && item.name && item.name.length && item.color);
            return (
                <div className={cx('bottom-panel', (!(creating || updating)) ? 'wcag-invisible' : '')}  
                    aria-hidden={!(creating || updating)} 
                    aria-labelledby= "Maak categorie aan">                   
                        {/* WCAG (button):
                        1) no role; they are buttons
                        2) focussable (Tab)
                        3) toggle function: n/a (when true use aria-checked)
                        4) aria-expanded: n/a
                        5) aria-haspopup: n/a  
                        6) aria-labelledby/aria-describedby: ref to label
                        7) icon of button is explained by tekst-> aria-hidden*/} 
                        <div className={cx('bottom-buttons')}>
                            <div className={cx('buttons-container', !(creating || updating)? 'wcag-invisible' : '' )}>                            
                                <Btn 
                                    type="button" 
                                    className={'btn'} 
                                    extraClassNames={'secondary-btn'}
                                    aria-label={`Annuleren van ingevoerde categorie`}
                                    aria-haspopup={false}                        
                                    tabIndex={0}  
                                    onKeyDown={(evt: any) => handleEnters(evt, ()=>onCancel())}                          
                                    onClick={()=> onCancel()}>
                                        <span>Annuleren</span>
                                </Btn>
        
                                <Btn 
                                    type="button"                                
                                    className={'btn'}
                                    disabled = {!canBeSaved}
                                    aria-label={`Opslaan van ingevoerde categorie`}
                                    aria-haspopup={false}                        
                                    onKeyDown={(evt: any) => handleEnters(evt, () => canBeSaved ? onSave(): null)}    
                                    onClick={() => canBeSaved ? onSave(): null}>
                                        <span>Opslaan</span>
                                </Btn>                 
                            </div>
                        </div>
                    </div>                    
    
            );
        });

function handleEnters(evt: any, funct: ()=>void) {
    if (evt.key==='Enter') {evt.preventDefault(); funct();}
}   
        
function fixNoteDevelopmentJsonServer(item: ICategory) {

    return item;
}



// WEBPACK FOOTER //
// (webpack)-remove-block-loader??ref--5-0!./src/note/categories/categoryEditArea.tsx